import type { Lane, TechTask } from './api';

export type LaneFilter = 'all' | Lane;

/**
 * ONE ROW OF THE WORKLIST — a single test, or a BUNDLE: every open test one
 * patient has from the same visit, done in one sitting and drawn as one row
 * ("Sara M. — bundle of 3"). Lane counts count rows, so a chip can never
 * disagree with the list it filters.
 */
export interface Item {
  id: string;
  bundle: boolean;
  tasks: TechTask[];
  /** The test the row's verb acts on — a bundle's next unfinished step. */
  next: TechTask;
  lane: Lane;
}

const closed = (t: TechTask) => t.state === 'done' || t.state === 'cant';

function laneOf(tasks: TechTask[]): Lane {
  if (tasks.every(closed)) return 'done';
  const open = tasks.filter((t) => !closed(t));
  if (open.some((t) => t.lane === 'with_you')) return 'with_you';
  if (open.some((t) => t.lane === 'ready')) return 'ready';
  if (open.some((t) => t.lane === 'waiting')) return 'waiting';
  return open[0]?.lane ?? 'ready';
}

/* "Sequenced by readiness, not by order time": urgent first, then what can be
   started now, what is waiting on a clock, what is with you, what is done. */
const LANE_ORDER: Record<Lane, number> = { ready: 0, waiting: 1, with_you: 2, done: 3, tomorrow: 4 };

export function buildItems(tasks: TechTask[]): Item[] {
  const byBundle = new Map<string, TechTask[]>();
  const singles: TechTask[] = [];
  for (const t of tasks) {
    if (t.bundle && t.bundle.size > 1 && t.lane !== 'tomorrow') {
      byBundle.set(t.bundle.key, [...(byBundle.get(t.bundle.key) ?? []), t]);
    } else singles.push(t);
  }
  const items: Item[] = [];
  for (const [key, group] of byBundle) {
    const sorted = [...group].sort((a, b) => (a.bundle?.index ?? 0) - (b.bundle?.index ?? 0));
    /* A bundle whose other members are not on this list (handed away, claimed
       by someone else) is not a bundle here — draw what is left as singles. */
    if (sorted.length < 2) {
      singles.push(...sorted);
      continue;
    }
    const next = sorted.find((t) => !closed(t)) ?? sorted[sorted.length - 1];
    items.push({ id: `b:${key}`, bundle: true, tasks: sorted, next, lane: laneOf(sorted) });
  }
  for (const t of singles) items.push({ id: t.id, bundle: false, tasks: [t], next: t, lane: t.lane });

  const urgent = (i: Item) => (i.tasks.some((t) => !closed(t) && t.priority !== 'routine') ? 0 : 1);
  return items.sort(
    (a, b) =>
      LANE_ORDER[a.lane] - LANE_ORDER[b.lane] ||
      urgent(a) - urgent(b) ||
      new Date(a.next.createdAt).getTime() - new Date(b.next.createdAt).getTime(),
  );
}

export function countLanes(items: Item[]): Record<LaneFilter, number> {
  /* All is today's list — Tomorrow is its own lane, not part of today's sum. */
  const c: Record<LaneFilter, number> = { all: 0, ready: 0, waiting: 0, with_you: 0, done: 0, tomorrow: 0 };
  for (const i of items) {
    c[i.lane] += 1;
    if (i.lane !== 'tomorrow') c.all += 1;
  }
  return c;
}

export function matches(item: Item, q: string): boolean {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return item.tasks.some((t) =>
    [t.title, t.kindLabel, t.patient?.firstName, t.patient?.lastName, t.patient?.mrn, t.requestedBy?.firstName]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(s),
  );
}

export const LANE_LABEL: Record<LaneFilter, string> = {
  all: 'All',
  ready: 'Ready to start',
  waiting: 'Waiting',
  with_you: 'With you',
  done: 'Done',
  tomorrow: 'Tomorrow',
};

export const isClosed = closed;
